import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { getStockAuditEvents, getStockEngines, AuditEvent, StockEngine } from '../../services/ttStockTraderService';
import '../Dashboard.css';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  green: '#16a34a', red: '#dc2626', amber: '#d97706', purple: '#8b5cf6',
};

const TYPE_COLOR: Record<string, string> = {
  trade_opened: c.green,
  trade_closed: c.purple,
  exit_taken: c.purple,
  signal_generated: c.amber,
  engine_run_start: c.muted,
  engine_run_complete: c.muted,
  error: c.red,
  submit_failed: c.red,
};

const fmtTime = (s?: string) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
  } catch { return s; }
};

const TtStockAudit: React.FC = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [engines, setEngines] = useState<StockEngine[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [engineFilter, setEngineFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true); setErr(null);
      const engineList = await getStockEngines(token);
      setEngines(engineList);
      if (engineList.length === 0) { setEvents([]); return; }
      // Fetch audit events per engine (the audit table is keyed by userId so
      // we filter by engineId server-side via the existing /algo/audit param).
      const perEngine = await Promise.all(
        engineList.map((e) => getStockAuditEvents(token, { engineId: e.engineId, limit: 200 }).catch(() => [])),
      );
      const merged = perEngine.flat().sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
      setEvents(merged);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load audit');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const engineName = (engineId?: string) => engineId ? (engines.find((e) => e.engineId === engineId)?.name || engineId.slice(0, 8)) : '—';

  const allTypes = useMemo(() => {
    const set = new Set<string>();
    events.forEach((e) => e.eventType && set.add(e.eventType));
    return Array.from(set).sort();
  }, [events]);

  const filtered = useMemo(() => events.filter((e) => {
    if (engineFilter !== 'all' && e.engineId !== engineFilter) return false;
    if (typeFilter !== 'all' && e.eventType !== typeFilter) return false;
    return true;
  }), [events, engineFilter, typeFilter]);

  const toggle = (id: string) => {
    setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  if (loading) return <div className="dashboard-container"><Navigation /><div className="dashboard-content" style={{ padding: 24, color: c.muted }}>Loading…</div></div>;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content" style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px', color: c.text }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>TT Stocks — Audit</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: c.muted }}>Per-event log from your TT stock engines (runs, picks, fills, exits, errors).</p>
          </div>
          <button onClick={load} style={{ padding: '6px 14px', background: c.purple, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>↻ Refresh</button>
        </div>

        {err && <div style={{ background: '#7f1d1d40', border: `1px solid ${c.red}`, color: '#fca5a5', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: c.muted }}>Engine:&nbsp;
            <select value={engineFilter} onChange={(e) => setEngineFilter(e.target.value)} style={{ background: '#111827', color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: '4px 8px' }}>
              <option value="all">All</option>
              {engines.map((e) => <option key={e.engineId} value={e.engineId}>{e.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: c.muted }}>Event type:&nbsp;
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ background: '#111827', color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: '4px 8px' }}>
              <option value="all">All</option>
              {allTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: c.dim }}>{filtered.length} of {events.length} events</span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ color: c.muted, fontSize: 14, padding: 16, background: c.cardAlt, borderRadius: 6 }}>No events match these filters.</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {filtered.map((e, i) => {
              const id = e.eventId || `${e.timestamp}-${i}`;
              const isOpen = expanded.has(id);
              const typeColor = TYPE_COLOR[e.eventType] || c.text;
              return (
                <div key={id} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6 }}>
                  <div onClick={() => toggle(id)} style={{
                    cursor: 'pointer', padding: '0.55rem 0.85rem',
                    display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) minmax(180px, 2fr) minmax(160px, 1fr) auto',
                    gap: 10, alignItems: 'center', fontSize: 13,
                  }}>
                    <div style={{ color: c.muted }}>{fmtTime(e.timestamp)}</div>
                    <div>
                      <span style={{ color: typeColor, fontWeight: 600 }}>{e.eventType}</span>
                      {e.symbol && <span style={{ color: c.text, marginLeft: 8 }}>{e.symbol}</span>}
                    </div>
                    <div style={{ color: c.muted, fontSize: 12 }}>{engineName(e.engineId)}</div>
                    <div style={{ color: c.dim }}>{isOpen ? '▼' : '▶'}</div>
                  </div>
                  {isOpen && (
                    <pre style={{
                      margin: 0, padding: '0 0.85rem 0.7rem', borderTop: `1px solid ${c.border}`,
                      background: c.bg, color: c.muted, fontSize: 11, lineHeight: 1.5,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    }}>{JSON.stringify(e.details || e, null, 2)}</pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TtStockAudit;
